import { documentalRemittanceInput, documentalUploadInput, type DocumentalRequirement, type DocumentalResponse, type PortalFileView } from "@shared/documentos-externos";
import { z } from "zod";

export type PortalRemittanceInput = z.infer<typeof documentalRemittanceInput>;
export const DOCUMENT_RESPONSE_LABELS: Record<DocumentalResponse["respostaExterna"], string> = {
  encaminhado: "Encaminhado", indisponivel: "Indisponível", nao_se_aplica: "Não se aplica",
};

export class DocumentalError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

async function documentalFetch(path: string, init?: RequestInit) {
  try { return await fetch(path, init); }
  catch { throw new Error("A conexão foi interrompida. Confira sua internet e tente novamente. O preenchimento permanece nesta tela."); }
}

/** Cookie documental e CSRF vivem separados da autenticação corporativa. */
export async function documentalRequest<T>(path: string, method = "GET", body?: unknown, csrf?: string): Promise<T> {
  const response = await documentalFetch(`/api/documentos${path}`, {
    method, credentials: "same-origin", cache: "no-store",
    headers: { ...(body !== undefined ? { "Content-Type": "application/json" } : {}), ...(csrf ? { "X-Documental-CSRF": csrf } : {}) },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) {
    let message = "Não foi possível concluir a operação. Tente novamente.";
    try { const data = await response.json(); if (typeof data.message === "string") message = data.message; } catch { /* Não exibir páginas de erro ou detalhes internos. */ }
    throw new DocumentalError(response.status, message);
  }
  return response.status === 204 ? undefined as T : response.json();
}

export function groupDocumentItems(items: DocumentalRequirement[]) {
  const groups = new Map<string, DocumentalRequirement[]>();
  for (const item of items) { const group = groups.get(item.categoria) ?? []; group.push(item); groups.set(item.categoria, group); }
  return Array.from(groups.entries()).map(([name, items]) => ({ name, items }));
}

export async function uploadDocument(file: File, csrf: string, requisitoId?: string, anteriorId?: string): Promise<PortalFileView> {
  const metadata = documentalUploadInput.safeParse({ nome: file.name, tamanho: file.size, tipo: file.type, ...(requisitoId ? { requisitoId } : {}), ...(anteriorId ? { anteriorId } : {}) });
  if (!metadata.success) throw new Error("Selecione um PDF, JPEG, PNG ou WebP de até 20 MB, com nome válido e conteúdo não vazio.");
  const upload = await documentalRequest<{ id: string }>("/uploads", "POST", metadata.data, csrf);
  const response = await documentalFetch(`/api/documentos/uploads/${upload.id}`, { method: "PUT", credentials: "same-origin", headers: { "Content-Type": file.type, "X-Documental-CSRF": csrf }, body: file });
  if (!response.ok) throw new DocumentalError(response.status, "O arquivo não foi transferido. Selecione-o novamente para tentar o envio.");
  return documentalRequest<PortalFileView>(`/uploads/${upload.id}/confirmar`, "POST", {}, csrf);
}

export async function downloadDocument(path: string, name: string, authenticated?: (path: string) => Promise<Response>) {
  const response = authenticated ? await authenticated(path) : await documentalFetch(path, { credentials: "same-origin", cache: "no-store" });
  if (!response.ok) throw new DocumentalError(response.status, "O arquivo não está disponível para esta sessão. Reabra o convite ou tente novamente.");
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement("a"); anchor.href = url; anchor.download = name; anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function documentalDate(value: string) { return new Date(value.length === 10 ? `${value}T12:00:00` : value).toLocaleString("pt-BR", value.length === 10 ? { dateStyle: "short" } : { dateStyle: "short", timeStyle: "short" }); }
